import { PolicyStore } from '@Policy/store/createStore';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import {
  ActionData,
  ActionEnum,
  ExchangeOnlinePolicyItem,
} from '@Policy/utils/types';
import i18nInst from 'src/i18n';

export interface AnomalyActionData {
  threatType: string;
  action: ActionEnum;
  setAction: (action: ActionEnum) => void;
  subjectTag: string;
  setSubjectTag: (tag: string) => void;
  disclaimer: string;
  setDisclaimer: (disclaimer: string) => void;
}

export const raiActionDataSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const {
    setRiskAiState,
    config: {
      featureShow: { gmailQtSettingFeatureShow },
    },
  } = state;
  const {
    policyType,
    tmRiskAiFilters: {
      raiActionConfirmed,
      raiSuspiciousTagConfirmed,
      raiConfirmedNotifyType,
      raiActionSpam,
      raiSuspiciousTagSpam,
      raiSpamNotifyType,
      // Super Aggressive
      raiSuperAggActionSuspicious,
      raiSuperAggActionUnwanted,
      raiSuperAggSubjectTagSuspicious,
      raiSuperAggSubjectTagUnwanted,
      raiSuperAggDisclaimerSuspicious,
      raiSuperAggDisclaimerUnwanted,
      // Aggressive
      raiAggActionSuspicious,
      raiAggActionUnwanted,
      raiAggSubjectTagSuspicious,
      raiAggSubjectTagUnwanted,
      raiAggDisclaimerSuspicious,
      raiAggDisclaimerUnwanted,
      // Conservative
      raiConsActionSuspicious,
      raiConsActionUnwanted,
      raiConsSubjectTagSuspicious,
      raiConsSubjectTagUnwanted,
      raiConsDisclaimerSuspicious,
      raiConsDisclaimerUnwanted,
    },
  } = template;
  const { t } = i18nInst;

  const isGmail = [PolicyTypeEnum.Gmail, PolicyTypeEnum.GmailInline].includes(
    policyType
  );

  const isInline = [
    PolicyTypeEnum.ExchangeInline,
    PolicyTypeEnum.GmailInline,
  ].includes(policyType);

  const isMonitorPolicy = [
    PolicyModeEnum.DefaultMonitorPolicy,
    PolicyModeEnum.DefaultXDRMonitorPolicy,
  ].includes(state.template.policyMode);

  const getGmailActionOptions = () => {
    if (isMonitorPolicy) return [ActionEnum.pass];

    return [
      ...(isInline ? [ActionEnum.tagSubject] : [ActionEnum.labelEmail]),
      ActionEnum.moveToSpam,
      ActionEnum.pass,
      ActionEnum.passWithoutLogging,
      ...(isInline || gmailQtSettingFeatureShow ? [ActionEnum.quarantine] : []),
    ];
  };

  const getExchangeActionOptions = () => {
    if (isMonitorPolicy) return [ActionEnum.pass];

    return [
      ActionEnum.tagSubject,
      ActionEnum.addDisclaimer,
      ActionEnum.moveToJunkEmailFolder,
      ActionEnum.quarantine,
      ActionEnum.pass,
      ActionEnum.passWithoutLogging,
    ];
  };

  const getSecurityRiskOptions = () => {
    if (isMonitorPolicy) return [ActionEnum.pass];

    if (isGmail) {
      return [
        ActionEnum.pass,
        ...(isInline ? [ActionEnum.tagSubject] : [ActionEnum.labelEmail]),
        ...(isInline ? [] : [ActionEnum.delete]),
        ...(isInline || gmailQtSettingFeatureShow
          ? [ActionEnum.quarantine]
          : []),
      ];
    }

    return [
      ActionEnum.pass,
      ActionEnum.tagSubject,
      ActionEnum.quarantine,
      ...(isInline ? [] : [ActionEnum.delete]),
    ];
  };

  const getSpamActionOptions = () => {
    if (isMonitorPolicy) return [ActionEnum.pass];

    if (isGmail) {
      return [
        ActionEnum.pass,
        ...(isInline ? [ActionEnum.tagSubject] : [ActionEnum.labelEmail]),
        ActionEnum.moveToSpam,
        ...(isInline || gmailQtSettingFeatureShow
          ? [ActionEnum.quarantine]
          : []),
        ...(isInline ? [] : [ActionEnum.delete]),
      ];
    }

    return [
      ActionEnum.pass,
      ActionEnum.tagSubject,
      ActionEnum.moveToJunkEmailFolder,
      ActionEnum.quarantine,
      ...(isInline ? [] : [ActionEnum.delete]),
    ];
  };

  const securityRiskActionOptions = getSecurityRiskOptions();
  const spamActionOptions = getSpamActionOptions();

  const allRulesActionOptions = isGmail
    ? getGmailActionOptions()
    : getExchangeActionOptions();

  const specifiedRulesActionOptions = isGmail
    ? getGmailActionOptions()
    : getExchangeActionOptions();

  const securityRiskActionData: ActionData[] = [
    {
      threatType: t('policy.threatType.riskAiSecurityRisk'),
      threatDescription: t('policy.threatDescription.riskAiSecurityRisk'),
      handler: setRiskAiState,
      action: raiActionConfirmed,
      actionKey: 'raiActionConfirmed',
      actions: securityRiskActionOptions,
      notifyType: raiConfirmedNotifyType,
      notifyTypeKey: 'raiConfirmedNotifyType',
      suspiciousTag: raiSuspiciousTagConfirmed,
      suspiciousTagKey: 'raiSuspiciousTagConfirmed',
    },
  ];

  const phishingActionData: ActionData[] = [
    {
      threatType: t('policy.actionData.phishing'),
      handler: setRiskAiState,
      action: raiActionConfirmed,
      actionKey: 'raiActionConfirmed',
      actions: securityRiskActionOptions,
      notifyType: raiConfirmedNotifyType,
      notifyTypeKey: 'raiConfirmedNotifyType',
      suspiciousTag: raiSuspiciousTagConfirmed,
      suspiciousTagKey: 'raiSuspiciousTagConfirmed',
    },
  ];

  const spamActionData: ActionData[] = [
    {
      threatType: t('policy.actionData.spam'),
      handler: setRiskAiState,
      action: raiActionSpam,
      actionKey: 'raiActionSpam',
      actions: spamActionOptions,
      notifyType: raiSpamNotifyType,
      notifyTypeKey: 'raiSpamNotifyType',
      suspiciousTag: raiSuspiciousTagSpam,
      suspiciousTagKey: 'raiSuspiciousTagSpam',
    },
  ];

  const extraAggressiveActionData: AnomalyActionData[] = [
    {
      threatType: t('policy.threatType.suspiciousEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiSuperAggActionSuspicious: action }),
      action: raiSuperAggActionSuspicious,
      subjectTag: raiSuperAggSubjectTagSuspicious,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiSuperAggSubjectTagSuspicious: tag }),
      disclaimer: raiSuperAggDisclaimerSuspicious,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiSuperAggDisclaimerSuspicious: disclaimer }),
    },
    {
      threatType: t('policy.threatType.possiblyUnwantedEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiSuperAggActionUnwanted: action }),
      action: raiSuperAggActionUnwanted,
      subjectTag: raiSuperAggSubjectTagUnwanted,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiSuperAggSubjectTagUnwanted: tag }),
      disclaimer: raiSuperAggDisclaimerUnwanted,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiSuperAggDisclaimerUnwanted: disclaimer }),
    },
  ];

  const aggressiveActionData: AnomalyActionData[] = [
    {
      threatType: t('policy.threatType.suspiciousEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiAggActionSuspicious: action }),
      action: raiAggActionSuspicious,
      subjectTag: raiAggSubjectTagSuspicious,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiAggSubjectTagSuspicious: tag }),
      disclaimer: raiAggDisclaimerSuspicious,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiAggDisclaimerSuspicious: disclaimer }),
    },
    {
      threatType: t('policy.threatType.possiblyUnwantedEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiAggActionUnwanted: action }),
      action: raiAggActionUnwanted,
      subjectTag: raiAggSubjectTagUnwanted,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiAggSubjectTagUnwanted: tag }),
      disclaimer: raiAggDisclaimerUnwanted,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiAggDisclaimerUnwanted: disclaimer }),
    },
  ];

  const moderateActionData: AnomalyActionData[] = [
    {
      threatType: t('policy.threatType.suspiciousEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiConsActionSuspicious: action }),
      action: raiConsActionSuspicious,
      subjectTag: raiConsSubjectTagSuspicious,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiConsSubjectTagSuspicious: tag }),
      disclaimer: raiConsDisclaimerSuspicious,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiConsDisclaimerSuspicious: disclaimer }),
    },
    {
      threatType: t('policy.threatType.possiblyUnwantedEmail'),
      setAction: (action: ActionEnum) =>
        setRiskAiState({ raiConsActionUnwanted: action }),
      action: raiConsActionUnwanted,
      subjectTag: raiConsSubjectTagUnwanted,
      setSubjectTag: (tag: string) =>
        setRiskAiState({ raiConsSubjectTagUnwanted: tag }),
      disclaimer: raiConsDisclaimerUnwanted,
      setDisclaimer: (disclaimer: string) =>
        setRiskAiState({ raiConsDisclaimerUnwanted: disclaimer }),
    },
  ];

  return {
    securityRiskActionData,
    phishingActionData,
    spamActionData,
    extraAggressiveActionData,
    aggressiveActionData,
    moderateActionData,
    allRulesActionOptions,
    specifiedRulesActionOptions,
  };
};

export const raiApprovedSenderEmailEntriesSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;

  const raiApprovedSenderEmailEntries =
    template.tmRiskAiFilters.raiApprovedSenderEmailEntries;
  const separator = ';';

  if (raiApprovedSenderEmailEntries === '') {
    return [];
  }

  const list = raiApprovedSenderEmailEntries
    .split(separator)
    .filter((item) => item.length > 0)
    .map((item) => ({
      name: item,
      value: item,
    }));

  return list;
};
